'use client'

import { AnimatedAccordion } from '@components/primitives/AnimatedAccordion'
import {
  type ArmGroupForTreatmentDoughnut,
  TreatmentDoughnut,
} from '@components/primitives/Chart/TreatmentDoughnut'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight'
import { romanize } from '@lib/utilities/number/romanize'
import { type TreatmentItem } from '@modules/interventions/helpers/parseTreatmentsFromTrial'
import { type ClinicalTrialForListingPage } from '@modules/trials/helpers/fragments/clinicalTrialListingFragment'
import { isOtsukaMdd } from '@modules/trials/helpers/isHardcodedTrial'
import { useState } from 'react'
import getArmGroupsForTrialDesign from './getArmGroupsForTrialDesign'

export const TrialDesign = ({
  trial,
}: {
  trial: Pick<
    ClinicalTrialForListingPage,
    'armGroups' | 'interventions' | 'nctId' | 'treatments' | 'trialLocations'
  >
}) => {
  const [openIndex, setAccordionOpen] = useState(-1)
  const armGroups = getArmGroupsForTrialDesign(trial)

  return (
    <div className='flex flex-wrap'>
      <div className='mb-2 flex w-full flex-col gap-8 rounded-xl border border-neutral200 bg-white p-4 lg:rounded-2xl lg:p-6'>
        <div>
          <TreatmentDoughnut
            armGroups={armGroups}
            selectedGroupIndex={openIndex}
            treatmentNameInLegend={isTreatmentNameInLegendWhitelist(
              trial.nctId,
            )}
          />
        </div>

        <div>
          {armGroups.map((armGroup, index) => (
            <ArmGroupAccordion
              armGroup={armGroup}
              index={index}
              key={armGroup.name}
              openIndex={openIndex}
              setAccordionOpen={setAccordionOpen}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const ArmGroupAccordion = ({
  armGroup,
  index,
  openIndex,
  setAccordionOpen,
}: {
  armGroup: ArmGroupForTreatmentDoughnut & {
    description: string | null
    treatments: TreatmentItem[]
  }
  index: number
  openIndex: number
  setAccordionOpen: (index: number) => void
}) => {
  const bgColor = getBgColor(armGroup.type)
  const accordionOpen = openIndex === index
  const hoverbgColor = getHoverBgColor(armGroup.type)

  return (
    <div
      className={classMerge(
        `cursor-pointer border-b border-neutral200 p-3 last:border-none ${hoverbgColor} hover:rounded-lg`,
        accordionOpen && bgColor,
        accordionOpen && 'rounded-lg border-none',
      )}
      onClick={() => {
        accordionOpen ? setAccordionOpen(-1) : setAccordionOpen(index)
      }}
    >
      <div className='flex items-center'>
        <Icon
          className={`mr-4 cursor-pointer ${
            accordionOpen ? 'rotate-90' : ''
          } transform transition-all duration-500`}
          icon={faChevronRight}
        />
        <div className='text-base font-semibold text-neutral600'>
          <span className='mr-2'>
            Group {romanize(index + 1)}: {armGroup.name}
          </span>
          <GroupBadge type={armGroup.type} />
          <span className='mx-2 text-sm font-medium text-neutral600'>
            {armGroup.treatments.length} Intervention
            {armGroup.treatments.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <AnimatedAccordion isExpanded={accordionOpen}>
        <Text
          className='mt-1 text-neutral600'
          styleName='p-small'
          value={armGroup.description ?? ''}
        />
      </AnimatedAccordion>
    </div>
  )
}

const getBgColor = (
  type: 'Active Control' | 'Experimental Treatment' | 'Placebo Group',
) => {
  if (type === 'Experimental Treatment') {
    return 'bg-blue50'
  } else if (type === 'Active Control') {
    return 'bg-green50'
  } else {
    return 'bg-neutral50'
  }
}

const getHoverBgColor = (
  type: 'Active Control' | 'Experimental Treatment' | 'Placebo Group',
) => {
  if (type === 'Experimental Treatment') {
    return 'hover:bg-blue50'
  } else if (type === 'Active Control') {
    return 'hover:bg-green50'
  } else {
    return 'hover:bg-neutral50'
  }
}

const getTextColor = (
  type: 'Active Control' | 'Experimental Treatment' | 'Placebo Group',
) => {
  if (type === 'Experimental Treatment') {
    return 'text-blue800'
  } else if (type === 'Active Control') {
    return 'text-green800'
  } else {
    return 'text-neutral800'
  }
}

const isTreatmentNameInLegendWhitelist = (nctId: string) => {
  const whiteList = [isOtsukaMdd(nctId)]
  return whiteList.some((item) => item)
}

const GroupBadge = ({
  type,
}: {
  type: 'Active Control' | 'Experimental Treatment' | 'Placebo Group'
}) => {
  const bgColor = getBgColor(type)
  const textColor = getTextColor(type)
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${bgColor} ${textColor}`}
    >
      {type}
    </span>
  )
}
